import { RibbonProps } from 'domain/constants/ribbons';
import { useHomePageJSON } from 'domain/selectors/ribbons';
import { isNilOrEmpty } from 'presentation/utils/generalUtils';
import React, { Suspense, lazy } from 'react';

const Helmet = lazy(() => import('react-helmet').then((module) => ({ default: module.Helmet })));

const normalizeUrl = (url: string) =>
  url.startsWith('/') ? `${window.location.protocol}//${window.location.host}${url}` : url;

// does not actually extend it, but importRibbon gets grumpy otherwise
interface OuterProps extends RibbonProps {
  robots?: string;
  canonicalUrl?: string;
  googleSiteVerification?: string;
  openGraph?: OpenGraphProps;
  fb?: {
    pages: string;
  };
  dcTerms?: {
    title: string;
    description: string;
  };
  domainVerify?: string;
  page: {
    title: string;
    description: string;
  };
}

interface OpenGraphProps {
  title?: string;
  type?: string;
  url?: string;
  image?: string;
  siteName?: string;
}

const renderOpenGraph = ({ title, type, url, image, siteName }: OpenGraphProps) => [
  Boolean(title) && <meta key="og:title" property="og:title" content={title} />,
  Boolean(type) && <meta key="og:type" property="og:type" content={type} />,
  Boolean(url) && (
    <meta key="og:url" property="og:url" content={`${window.location.protocol}//${window.location.host}${url}`} />
  ),
  Boolean(image) && <meta key="og:image" property="og:image" content={image} />,
  Boolean(siteName) && <meta key="og:site_name" property="og:site_name" content={siteName} />,
];

const PureMetaRibbon = ({
  page,
  robots,
  openGraph,
  canonicalUrl,
  googleSiteVerification,
  fb,
  domainVerify,
  dcTerms,
}: OuterProps) => {
  const parsedOpenGraph = {
    ...openGraph,
    title: openGraph?.title ?? page.title,
  };
  const parsedDcTerms = {
    title: dcTerms?.title ?? page.title,
    description: dcTerms?.description ?? page.description,
  };
  const homepageData = useHomePageJSON();

  return (
    <Suspense fallback={null}>
      {/* @ts-ignore: 🤷‍♂️ */}
      <Helmet>
        {Boolean(page && page.title) && <title>{page.title}</title>}
        {Boolean(page && page.description) && <meta name="description" content={page.description} />}
        {robots && robots !== 'INDEX' && robots !== 'INDEX FOLLOW' && typeof robots === 'string' && (
          <meta name="robots" content={robots.replace(/ /g, ', ')} />
        )}
        {canonicalUrl != null && <link href={normalizeUrl(canonicalUrl)} rel="canonical" />}
        {Boolean(googleSiteVerification) && <meta name="google-site-verification" content={googleSiteVerification} />}
        {Boolean(domainVerify) && <meta name="p:domain_verify" content={domainVerify} />}
        {fb?.pages != null && <meta property="fb:pages" content={fb.pages} />}
        {Boolean(parsedDcTerms) && [
          Boolean(parsedDcTerms.title) && <meta name="DCTERMS.title" content={parsedDcTerms.title} />,
          Boolean(parsedDcTerms.description) && <meta name="DCTERMS.description" content={parsedDcTerms.description} />,
        ]}
        {renderOpenGraph(parsedOpenGraph)}
        {!isNilOrEmpty(homepageData) && <script type="application/ld+json">{JSON.stringify(homepageData)}</script>}
      </Helmet>
    </Suspense>
  );
};

PureMetaRibbon.displayName = 'MetaRibbon';

export default PureMetaRibbon;
